// src/components/about/motion.ts
// Single source of truth for the About page's motion language.
//
// Every section imports its easing, durations and stagger from here so the
// page reads as one animation system instead of five independently tuned ones.
// Rules encoded in this file:
//   - One easing curve, one duration per intent, one stagger per density.
//   - Nothing bounces. No spring, no overshoot.
//   - Entrances only. No looping, no idle/floating motion.
//   - Entrance reveals use `transform` + `opacity` only, so reveals never
//     trigger layout or paint of surrounding content (no layout shift).
import type { Variants } from 'framer-motion';

/** Decelerating curve. The same value is used by the CSS transitions in the
 *  section style files, so JS and CSS motion share one easing language. */
export const EASE_OUT: [number, number, number, number] = [0.22, 1, 0.36, 1];

/** Duration scale in seconds, keyed by intent rather than by value. */
export const DURATION = {
  /** Content entering as it scrolls into relevance. */
  reveal: 0.45,
  /** Reduced-motion reveal: opacity only, in place, no travel. */
  revealReduced: 0.3,
  /** A panel swapping in for the one it replaces. */
  panelIn: 0.28,
  /** A panel leaving. Deliberately quicker than entering. */
  panelOut: 0.18,
  /** Reduced-motion panel swap. */
  panelInReduced: 0.15,
  panelOutReduced: 0.1,
  /** Height morphs, e.g. the mobile accordions. */
  morph: 0.3,
  /** Micro-interactions: hover, focus, chevrons, small state flips. */
  micro: 0.2,
  /** Micro-interactions with motion reduced. */
  microReduced: 0.12,
} as const;

/** Stagger step between siblings, in seconds. */
export const STAGGER = {
  /** Sparse groups such as section headers. */
  group: 0.07,
  /** Dense sequences such as pipeline nodes. */
  list: 0.05,
} as const;

/**
 * Parent variant that sequences its children and never animates itself.
 * Applying this to a non-motion parent would do nothing, so it is only ever
 * attached to elements that are also `motion.*` components.
 */
export const staggerGroup = (stagger: number, delayChildren = 0): Variants => ({
  hidden: {},
  visible: { transition: { staggerChildren: stagger, delayChildren } },
});

/** Standard content reveal: fade with a short rise. */
export const revealItem: Variants = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: DURATION.reveal, ease: EASE_OUT },
  },
};

/** Reduced-motion reveal: fades in place, no translation. */
export const revealItemReduced: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: DURATION.revealReduced } },
};

/** Panel swapping in over the outgoing panel. */
export const panelIn: Variants = {
  hidden: { opacity: 0, y: 8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: DURATION.panelIn, ease: EASE_OUT },
  },
  exit: {
    opacity: 0,
    y: -6,
    transition: { duration: DURATION.panelOut, ease: EASE_OUT },
  },
};

/** Reduced-motion panel swap. */
export const panelInReduced: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: DURATION.panelInReduced } },
  exit: { opacity: 0, transition: { duration: DURATION.panelOutReduced } },
};

/** Small scale-in, used only for the pipeline node markers. */
export const markerIn: Variants = {
  hidden: { opacity: 0, scale: 0.82 },
  visible: { opacity: 1, scale: 1 },
};

export interface MotionSet {
  /** Variant set for in-view content reveals. */
  reveal: Variants;
  /** Variant set for panels that replace one another. */
  panel: Variants;
  /** Parent sequencing variant, with motion already reduced if needed. */
  group: (stagger?: number, delayChildren?: number) => Variants;
  /** Stagger delay for one item, collapsed to 0 when motion is reduced. */
  offset: (index: number, step?: number) => number;
  /** Micro-interaction duration, shortened when motion is reduced. */
  micro: number;
  /** Duration for height/opacity morphs. */
  morph: number;
}

/**
 * Resolves the correct variant set for the caller's motion preference in one
 * call, so no component has to hand-roll the reduced-motion ternaries.
 */
export const motionSet = (reduceMotion: boolean): MotionSet => ({
  reveal: reduceMotion ? revealItemReduced : revealItem,
  panel: reduceMotion ? panelInReduced : panelIn,
  group: (stagger = STAGGER.group, delayChildren = 0) =>
    staggerGroup(reduceMotion ? 0 : stagger, reduceMotion ? 0 : delayChildren),
  offset: (index, step = STAGGER.group) => (reduceMotion ? 0 : index * step),
  micro: reduceMotion ? DURATION.microReduced : DURATION.micro,
  morph: reduceMotion ? DURATION.panelInReduced : DURATION.morph,
});
